// React
import { ReactElement, useState } from 'react';
// Modules
import classNames from 'classnames/bind';
import ResizeObserver from 'react-resize-observer';
// Features
import QualifiersMobileContent from '@utmb/features/qualifiers/qualifiers-mobile/QualifiersMobileContent';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Link } from '@utmb/design-system/link/link';
// Interfaces
import { ColorTypesEnum } from '@utmb/types/colors';
import { IconsEnum } from '@utmb/types/icons';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import SearchQualifiersRaceView from '@utmb/types/view/SearchQualifiersRaceView';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { useTranslation } from 'next-i18next';
import { QualifierIcon } from '@utmb/features/qualifiers/QualifierIcon';
import QualifierStat from '@utmb/features/qualifiers/qualifiers-stat/QualifierStat';
// Styles
import styles from './qualifiers-cards.module.scss';

const cx = classNames.bind(styles);

const QualifiersCards = (props: SearchQualifiersRaceView): ReactElement => {
    const { category, distance, elevationGain, eventName, name, startDate, startPlace, url, hasResults, uriResults } = props;

    const { t } = useTranslation();

    const dateWords = startDate && startDate.split('-');

    const [qualifierCardIsOpen, setQualifierCardIsOpen] = useState(false);

    const categories = {
        [PiCategoryEnum.GENERAL]: { borderClass: styles.border_index, textClass: styles.color_index, bgcolClass: styles.bgcol_index },
        [PiCategoryEnum.PI20K]: { borderClass: styles.border_20k, textClass: styles.color_20k, bgcolClass: styles.bgcol_20k },
        [PiCategoryEnum.PI50K]: { borderClass: styles.border_50k, textClass: styles.color_50k, bgcolClass: styles.bgcol_50k },
        [PiCategoryEnum.PI100K]: { borderClass: styles.border_100k, textClass: styles.color_100k, bgcolClass: styles.bgcol_100k },
        [PiCategoryEnum.PI100M]: { borderClass: styles.border_100m, textClass: styles.color_100m, bgcolClass: styles.bgcol_100m },
    };

    const cardCSS = cx(styles.card_container, category ? categories[category].borderClass : styles.border_index);
    const dateCSS = cx(styles.date, category ? categories[category].textClass : styles.color_index);
    const categoryStatCSS = cx('col-6', 'col-xl-3', styles.icon_wrapper, styles.spacing_bottom);
    const startPlaceStatCSS = cx('col-7', 'col-lg-6', 'col-xl-4', styles.stat_wrapper, styles.spacing_bottom);

    return (
        <>
            <div className={cardCSS} style={{ borderColor: category ? categories[category].borderClass : getColorStyle(ColorTypesEnum.WS_SECONDARY) }}>
                <div className="row">
                    <ResizeObserver
                        onResize={() => {
                            if (qualifierCardIsOpen && window.innerWidth >= 992) setQualifierCardIsOpen(false);
                        }}
                    />
                    <div className="col-3 col-lg-2">
                        <div className={dateCSS}>
                            {dateWords.length === 3 && (
                                <>
                                    <Font.OxaniumBold mobile="20" desktop="22" className="lh-10">
                                        {dateWords[1]}
                                    </Font.OxaniumBold>
                                    <Font.FuturaHeavy mobile="32" desktop="56">
                                        {dateWords[2]}
                                    </Font.FuturaHeavy>
                                    <Font.OxaniumBold mobile="20" desktop="22" className="lh-10">
                                        {dateWords[0]}
                                    </Font.OxaniumBold>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="col-9 col-lg-10">
                        <div className={`row ${styles.content_container}`}>
                            <div className="col-11 col-lg-8 col-xl-9">
                                <div className={styles.content_wrapper}>
                                    <div className={styles.top_content_container}>
                                        {name && (
                                            <div className={styles.race_name}>
                                                <Font.FuturaHeavy mobile="18" desktop="32">
                                                    {name}
                                                </Font.FuturaHeavy>
                                            </div>
                                        )}
                                        {eventName && (
                                            <div className={styles.city_name}>
                                                <Font.FuturaBook mobile="14" desktop="18">
                                                    {eventName}
                                                </Font.FuturaBook>
                                            </div>
                                        )}
                                    </div>
                                    <div className={`row ${styles.bottom_content_container}`}>
                                        {category && (
                                            <div className={categoryStatCSS}>
                                                <QualifierIcon icon={category} />
                                            </div>
                                        )}

                                        <div className={`col-6 ${styles.open_detail_toggler}`}>
                                            <button onClick={() => setQualifierCardIsOpen(!qualifierCardIsOpen)} className={styles.wscc_button_chevron}>
                                                {qualifierCardIsOpen && (
                                                    <>
                                                        <Font.FuturaBook mobile="14">{t('races.showLess')}</Font.FuturaBook>{' '}
                                                        <Icon variant={IconsEnum.CHEVRON_UP} clss={styles.wscc_icon_chevron} />
                                                    </>
                                                )}
                                                {!qualifierCardIsOpen && (
                                                    <>
                                                        <Font.FuturaBook mobile="14">{t('races.showMore')}</Font.FuturaBook>{' '}
                                                        <Icon variant={IconsEnum.CHEVRON_DOWN} clss={styles.wscc_icon_chevron} />
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {startPlace && (
                                            <div className={startPlaceStatCSS}>
                                                <QualifierStat isPlace label={t('qualifier.place')} value={startPlace} />
                                            </div>
                                        )}

                                        {distance && (
                                            <div className="col-lg-6 col-xl-2">
                                                <div className={`${styles.stat_wrapper} ${styles.hide_in_mobile}`}>
                                                    <QualifierStat
                                                        label={t('qualifier.distance.label')}
                                                        value={`${distance}${t('qualifier.distance.sufix')}`}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {elevationGain && (
                                            <div className="col-lg-6 col-xl-3">
                                                <div className={`${styles.stat_wrapper} ${styles.hide_in_mobile}`}>
                                                    <QualifierStat
                                                        label={t('qualifier.elevation.label')}
                                                        value={`${elevationGain}${t('qualifier.elevation.sufix')}`}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={`col-lg-4 col-xl-3 ${styles.visit_button}`}>
                                {url && (
                                    <Link.Button
                                        label={t('qualifier.visitButton.website')}
                                        href={url}
                                        color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                        bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                    />
                                )}
                                {hasResults && (
                                    <Link.Button
                                        label={t('qualifier.visitButton.race')}
                                        href={`/utmb-index/races/${uriResults}`}
                                        color={getColorStyle(ColorTypesEnum.WS_SECONDARY)}
                                        bgcolor={getColorStyle(ColorTypesEnum.WS_PRIMARY)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {qualifierCardIsOpen && <QualifiersMobileContent {...props} />}
        </>
    );
};

export default QualifiersCards;
